import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Check, X, Loader2 } from 'lucide-react';
import { notifyBarRequestApproved, notifyBarRequestRejected } from '../lib/pushNotifications';
import { getMetroById } from '../data/metros';
import { geocodeBarAddress, inferMetroForRequest } from '../utils/barGeocode';
import { isBroaderMetroNeighborhood, parseTownFromAddress } from '../utils/barLocation';

async function insertBar(row) {
  const result = await supabase.from('bars').insert(row).select().single();
  if (result.error && /metro_id|city|state/i.test(result.error.message || '')) {
    const { metro_id, city, state, ...legacyRow } = row;
    return supabase.from('bars').insert(legacyRow).select().single();
  }
  return result;
}

function ApproveBarRequest() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [barName, setBarName] = useState('');

  useEffect(() => {
    const approveBar = async () => {
      const requestId = searchParams.get('id');
      const action = searchParams.get('action'); // 'approve' or 'reject'
      const token = searchParams.get('token');

      if (!requestId || !action || !token) {
        setStatus('error');
        setMessage('Invalid request. Missing required parameters.');
        return;
      }

      if (!['approve', 'reject'].includes(action)) {
        setStatus('error');
        setMessage('Invalid action. Must be "approve" or "reject".');
        return;
      }

      try {
        // First, verify the token and get the bar request
        const { data: barRequest, error: fetchError } = await supabase
          .from('bar_requests')
          .select('*')
          .eq('id', requestId)
          .eq('approval_token', token)
          .single();

        if (fetchError || !barRequest) {
          setStatus('error');
          setMessage('Invalid or expired approval link.');
          return;
        }

        setBarName(barRequest.name);

        if (barRequest.status !== 'pending') {
          setStatus('error');
          setMessage(`This request has already been ${barRequest.status}.`);
          return;
        }

        if (action === 'approve') {
          const metro = getMetroById(barRequest.metro_id) || inferMetroForRequest(barRequest);
          const geocoded = await geocodeBarAddress(barRequest.address, {
            metro,
            neighborhood: barRequest.neighborhood,
          });
          if (!geocoded) {
            console.warn('All geocoding attempts failed for address:', barRequest.address);
          }
          const { lat, lng } = geocoded || metro.center;
          const city = isBroaderMetroNeighborhood(barRequest.neighborhood)
            ? (barRequest.city || parseTownFromAddress(barRequest.address) || metro.name)
            : metro.name;

          const { data: newBar, error: barError } = await insertBar({
            name: barRequest.name,
            address: barRequest.address,
            neighborhood: barRequest.neighborhood,
            description: barRequest.description,
            lat,
            lng,
            attributes: barRequest.attributes || {},
            metro_id: metro.id,
            city,
            state: metro.state,
          });

          if (barError) {
            console.error('Error creating bar:', barError);
            setStatus('error');
            setMessage(`Failed to create bar: ${barError.message}`);
            return;
          }

          // Update request status
          await supabase
            .from('bar_requests')
            .update({ status: 'approved', reviewed_at: new Date().toISOString() })
            .eq('id', requestId);

          // Notify the user who submitted the request
          if (barRequest.submitted_by) {
            await supabase.from('notifications').insert({
              user_id: barRequest.submitted_by,
              type: 'bar_request_approved',
              reference_id: newBar.id, // Reference to the new bar
              data: { bar_name: barRequest.name, bar_id: newBar.id }
            });

            // Send push notification
            notifyBarRequestApproved(barRequest.submitted_by, barRequest.name).catch(console.error);

            // Increment the user's bars_approved_count for badge tracking
            await supabase.rpc('increment_bars_approved_count', {
              p_user_id: barRequest.submitted_by
            });
          }

          setStatus('success');
          setMessage(`"${barRequest.name}" has been approved and added to Tilted!`);
        } else {
          // Reject the request
          await supabase
            .from('bar_requests')
            .update({ status: 'rejected', reviewed_at: new Date().toISOString() })
            .eq('id', requestId);

          // Notify the user who submitted the request
          if (barRequest.submitted_by) {
            await supabase.from('notifications').insert({
              user_id: barRequest.submitted_by,
              type: 'bar_request_rejected',
              data: { bar_name: barRequest.name }
            });

            // Send push notification
            notifyBarRequestRejected(barRequest.submitted_by, barRequest.name).catch(console.error);
          }

          setStatus('success');
          setMessage(`"${barRequest.name}" has been rejected.`);
        }
      } catch (error) {
        console.error('Error:', error);
        setStatus('error');
        setMessage(`An error occurred: ${error.message}`);
      }
    };

    approveBar();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-dive-black flex items-center justify-center p-4">
      <div className="bg-dive-dark border border-dive-gray rounded-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-dive-neon animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-display text-white mb-2">Processing...</h1>
            <p className="text-gray-400">Please wait while we process your request.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-display text-white mb-2">🍺 Tilted</h1>
            <p className="text-white text-lg mb-4">{message}</p>
            <button
              onClick={() => navigate('/admin')}
              className="btn-primary w-full"
            >
              Go to Admin Dashboard
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-display text-white mb-2">🍺 Tilted</h1>
            <p className="text-white text-lg mb-4">{message}</p>
            <button
              onClick={() => navigate('/admin')}
              className="btn-primary w-full"
            >
              Go to Admin Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ApproveBarRequest;
